import React from 'react';

import moment from 'moment';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CheckboxField from 'components/application/CheckboxField';
import CircleQuestion from 'components/application/CircleQuestion';
import CollectionSelect from 'components/application/CollectionSelect';
import ErrorMessage from 'components/application/ErrorMessage';
import RadioField from 'components/application/RadioField';
import TextField from 'components/application/TextField';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import CustomELearningCourseCard from 'components/courses/CustomELearningCourseCard';
import CourseSidePanelAutoEnrolmentForm from 'components/courses/sidepanel/CourseSidePanelAutoEnrolmentForm';
import CourseSidePanelProviderForm from 'components/courses/sidepanel/CourseSidePanelProviderForm';

import { sendAnalytics } from 'components/helpers/analytics';
import {
  currencyNumberToCurrencyString,
  displayCurrencyString,
} from 'components/helpers/currency';
import { formatDuration } from 'components/helpers/dates';
import { resourcesAsOptions } from 'components/helpers/forms';
import { resourceShape } from 'components/helpers/serialisableResources';

const renewalIntervalValues = ['day', 'week', 'month', 'year'];

export default function CourseSidePanelForm({
  assignableRoles,
  bodyRef,
  currentCourse,
  eLearningCourse,
  onCourseCompanyRoleDelete,
  onCourseCompanyRoleRequiredChange,
  onCourseRolesOptionChange,
  onELearningProviderSelectedChange,
  onInputChange,
  onOptionChange,
  removeErrorStyling,
  requestError,
}) {
  const currentActor = useCurrentActor();

  const { isCourseExpiryDateEditable } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelForm',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const convertValue = (value) => value === 'true';

  const expiryComparisonValuesAreSet =
    currentCourse.renewalFrequency &&
    currentCourse.renewalInterval &&
    currentCourse.expiringDuration;

  const shouldShowExpiryLengthWarning =
    expiryComparisonValuesAreSet &&
    moment.duration(
      currentCourse.renewalFrequency,
      currentCourse.renewalInterval,
    ) < moment.duration(currentCourse.expiringDuration, 'days');

  // filter out already selected courses
  const selectedRoleIds =
    currentCourse.courseCompanyRoles ?
      currentCourse.courseCompanyRoles.map((ccr) => ccr.companyRoleId)
    : [];

  const selectableRoles = assignableRoles.filter(
    (role) => !selectedRoleIds.includes(role.id),
  );

  const isELearningCustom = eLearningCourse?.attributes.custom;

  const { currencyCode } = currentActor.division.attributes;

  const eLearningCourseCost =
    isELearningCustom &&
    currencyNumberToCurrencyString({
      number: eLearningCourse.attributes.cost,
      currencyCode,
    });

  // order courseCompanyRoles alphabetically
  const orderedCourseCompanyRoles = currentCourse.courseCompanyRoles
    .map((courseCompanyRole) => {
      const roleId = courseCompanyRole.companyRoleId;
      const rolePosition = assignableRoles.find((role) => role.id === roleId)
        .attributes.position;
      return {
        roleId,
        rolePosition,
        required: courseCompanyRole.required,
        requirementPriorityCount: courseCompanyRole.requirementPriorityCount,
      };
    })
    .sort((a, b) =>
      a.rolePosition.localeCompare(b.rolePosition, 'en', {
        sensitivity: 'base',
      }),
    );

  const isCourseExpiryDateReminderEditable = isCourseExpiryDateEditable;

  const renewalIntervalOptions = renewalIntervalValues.map((value) => ({
    label: i18nTCommon(`shared.intervals.${pluralize(value)}`),
    value,
  }));

  return (
    <>
      <div className='popup__body-form tw-mb-8 tw-pb-0'>
        <ErrorMessage
          isFallback={requestError.isFallback}
          validationErrors={requestError.validationErrors}
          wrapperClassName='form-container'
        />
        <div className='form-container'>
          <TextField
            fieldError={requestError.validationErrors.name}
            isRequired={true}
            label={i18nTCommon('courses.courseName')}
            name='name'
            onChange={onInputChange}
            removeErrorStyling={removeErrorStyling}
            value={currentCourse.name}
          />
        </div>
      </div>

      <hr className='tw-m-0 tw-h-px tw-border-0 tw-bg-grey-100' />

      <div className='popup__body-form tw-mb-8 tw-pb-0'>
        <div className='form-container'>
          <label className='field__label tw-font-medium'>
            {i18nT('expires.label')}
          </label>
          <RadioField
            checked={currentCourse.expires}
            label={i18nT('expires.radioFields.expires.label')}
            name='expires'
            onChange={(event) => onInputChange(event, convertValue)}
            value={true}
          />
          {currentCourse.expires && (
            <div className='tw-ml-7'>
              <div className='side-panel__inline-container'>
                <div className='field--tab-inline tw-mr-1.5'>
                  <TextField
                    fieldError={requestError.validationErrors.renewalFrequency}
                    isDisabled={false}
                    isRequired={false}
                    label={i18nTCommon('courses.renewalFrequency')}
                    name='renewalFrequency'
                    onChange={onInputChange}
                    removeErrorStyling={removeErrorStyling}
                    value={currentCourse.renewalFrequency}
                  />
                </div>
                <div className='field--tab-inline tw-ml-1.5'>
                  <CollectionSelect
                    isRequired={true}
                    name='renewalInterval'
                    onChange={onOptionChange}
                    options={renewalIntervalOptions}
                    value={renewalIntervalOptions.find(
                      (option) =>
                        option.value === currentCourse.renewalInterval,
                    )}
                  />
                </div>
              </div>
              <div className='tw-mb-6 tw-inline-flex tw-gap-2'>
                <div className='tw-w-35'>
                  <TextField
                    customMargin='tw-mb-0'
                    fieldError={requestError.validationErrors.expiringDuration}
                    inputClassNames='tw-w-18'
                    isDisabled={!isCourseExpiryDateReminderEditable}
                    isRequired={false}
                    label={i18nT('expiringDuration.label')}
                    min={1}
                    name='expiringDuration'
                    onChange={(e) => {
                      sendAnalytics('Course expiry reminder edited', {
                        currentUser: currentActor.user,
                      });

                      onInputChange(e);
                    }}
                    removeErrorStyling={removeErrorStyling}
                    tooltip={i18nT(
                      `expiringDuration.tooltip.${isCourseExpiryDateReminderEditable ? 'default' : 'upgrade'}`,
                    )}
                    type='number'
                    value={currentCourse.expiringDuration}
                  />
                </div>
                <div className='tw-content-end'>
                  <div className='tw-h-10 tw-content-center tw-text-m'>
                    {i18nT('expiringDuration.description')}
                  </div>
                </div>
              </div>
              {eLearningCourse && (
                <div className='tw-mb-6 tw-flex'>
                  <CheckboxField
                    checked={currentCourse.certificateExpiryDateIncluded}
                    className='tw-w-auto'
                    label={i18nT('certificateExpiryDateIncluded.label')}
                    name='certificateExpiryDateIncluded'
                    onChange={onInputChange}
                  />
                  <div className='tw-ml-2'>
                    <Tooltip
                      className='tooltip-dark--max-w-xxs'
                      placement='top'
                      tooltip={i18nT('certificateExpiryDateIncluded.tooltip')}
                      trigger='hover'
                    >
                      <CircleQuestion />
                    </Tooltip>
                  </div>
                </div>
              )}
              {shouldShowExpiryLengthWarning && (
                <div className='m-0 tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
                  {i18nT('certificateExpiryDateIncluded.warning')}
                </div>
              )}
            </div>
          )}
          <RadioField
            checked={!currentCourse.expires}
            label={i18nT('expires.radioFields.doesNotExpire.label')}
            name='expires'
            onChange={(event) => onInputChange(event, convertValue)}
            value={false}
          />
        </div>
      </div>

      <hr className='tw-m-0 tw-h-px tw-border-0 tw-bg-grey-100' />

      <div className='popup__body-form tw-mb-8 tw-pb-0'>
        <div className='form-container'>
          <label
            className='field__label tw-font-medium'
            htmlFor='requiresEvidence'
          >
            {i18nTCommon('shared.evidence')}
            <Tooltip
              placement='top'
              tooltip={i18nTCommon('shared.egCertificatesOrCards')}
              trigger='hover'
            >
              <CircleQuestion />
            </Tooltip>
          </label>
          <RadioField
            checked={currentCourse.requiresEvidence}
            label={i18nT('requiresEvidence.radioFields.requiresEvidence.label')}
            name='requiresEvidence'
            onChange={(event) => onInputChange(event, convertValue)}
            value={true}
          />
          <RadioField
            checked={!currentCourse.requiresEvidence}
            label={i18nT(
              'requiresEvidence.radioFields.doesNotRequireEvidence.label',
            )}
            name='requiresEvidence'
            onChange={(event) => onInputChange(event, convertValue)}
            value={false}
          />
        </div>
      </div>

      <hr className='tw-m-0 tw-h-px tw-border-0 tw-bg-grey-100' />

      <div className='popup__body-form tw-mb-8 tw-pb-0'>
        <div className='form-container'>
          <CollectionSelect
            externalProps={{
              additionalNoOptionsMessage: i18nT(
                'companyRoleIds.additionalNoOptionsMessage',
              ),
            }}
            isSearchable={true}
            label={i18nT('companyRoleIds.label')}
            modifiers={['expandable']}
            name='companyRoleIds'
            onChange={(e) => onCourseRolesOptionChange(e.value.toString())}
            options={resourcesAsOptions({
              resourcesCollection: selectableRoles,
              resourceIdentifier: 'position',
            })}
            placeholder={i18nTCommon('roles.searchRoles')}
            value={null}
          />

          {currentActor.isAllowedFeature('training_register') && (
            <div className='popup__body-form tw-mb-8 tw-p-0'>
              <div className='form-container'>
                {orderedCourseCompanyRoles.map((courseCompanyRole) => (
                  <div
                    className='tw-group/pill chip-item-pill tw-relative tw-mb-2 tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-bg-grey-050 tw-px-3 tw-py-2.5'
                    key={courseCompanyRole.roleId}
                  >
                    <div className='tw-flex tw-flex-col'>
                      <div className='tw-text-s tw-font-medium tw-tracking-wide'>
                        {courseCompanyRole.rolePosition}
                      </div>
                      {courseCompanyRole.requirementPriorityCount > 0 && (
                        <div className='tw-text-s tw-tracking-wide tw-text-grey-700'>
                          {i18nT('overrideCount', {
                            count: courseCompanyRole.requirementPriorityCount,
                          })}
                        </div>
                      )}
                    </div>
                    <div className='tw-flex tw-items-center'>
                      <div className='tw-w-28'>
                        <CollectionSelect
                          modifiers={['pill']}
                          name='requiredCCR'
                          onChange={(e) =>
                            onCourseCompanyRoleRequiredChange(
                              courseCompanyRole.roleId,
                              e.value,
                            )
                          }
                          options={[
                            {
                              value: true,
                              label: i18nTCommon('shared.required'),
                            },
                            {
                              value: false,
                              label: i18nTCommon('shared.optional'),
                            },
                          ]}
                          value={
                            courseCompanyRole.required ?
                              {
                                value: true,
                                label: i18nTCommon('shared.required'),
                              }
                            : {
                                value: false,
                                label: i18nTCommon('shared.optional'),
                              }
                          }
                        />
                      </div>
                      <span
                        className="collection-select__multivalue-remove circle--remove tw-ml-2 tw-bg-transparent before:tw-bg-grey-700 before:tw-content-[''] after:tw-bg-grey-700 after:tw-content-[''] hover:tw-bg-red-600 hover:before:tw-bg-white hover:after:tw-bg-white"
                        onClick={() => {
                          onCourseCompanyRoleDelete(courseCompanyRole.roleId);
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <hr className='tw-m-0 tw-h-px tw-border-0 tw-bg-grey-100' />

      <div className='popup__body-form tw-mb-8 tw-pt-10'>
        {isELearningCustom ?
          <>
            <label className='field__label tw-mb-4 tw-font-medium'>
              {i18nT('customELearning.label')}
              <Tooltip
                className='tooltip-dark--max-w-xxs'
                placement='top'
                tooltip={i18nT('customELearning.tooltip')}
                trigger='hover'
              >
                <CircleQuestion />
              </Tooltip>
            </label>
            <CustomELearningCourseCard
              courseCost={displayCurrencyString({
                string: eLearningCourseCost,
                currencyCode,
              })}
              courseDuration={
                eLearningCourse.attributes.durationParts ?
                  formatDuration({
                    durationParts: eLearningCourse.attributes.durationParts,
                  })
                : ''
              }
              courseName={currentCourse.name}
              courseProvider={currentCourse.provider}
            />
            {currentActor.isAllowedFeature([
              'e_learning_auto_enrol',
              'e_learning',
              'training_register',
            ]) && (
              <CourseSidePanelAutoEnrolmentForm
                autoEnrolCourseRequirementScope={
                  currentCourse.autoEnrolCourseRequirementScope
                }
                autoEnrolEnrolleeScope={currentCourse.autoEnrolEnrolleeScope}
                hasAutoEnrolBeenActivelySelected={
                  currentCourse.hasAutoEnrolBeenActivelySelected
                }
                isExpiringCourse={currentCourse.expires}
                onInputChange={onInputChange}
                onOptionChange={onOptionChange}
              />
            )}
          </>
        : <CourseSidePanelProviderForm
            bodyRef={bodyRef}
            course={currentCourse}
            onELearningProviderSelectedChange={
              onELearningProviderSelectedChange
            }
            onInputChange={onInputChange}
            onOptionChange={onOptionChange}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
          />
        }
      </div>
    </>
  );
}

CourseSidePanelForm.propTypes = {
  currentCourse: PropTypes.object.isRequired,
  eLearningCourse: resourceShape('eLearningCourse'),
  requestError: PropTypes.object.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onOptionChange: PropTypes.func.isRequired,
  onELearningProviderSelectedChange: PropTypes.func.isRequired,
  assignableRoles: PropTypes.array.isRequired,
  bodyRef: PropTypes.object.isRequired,
};
